"use client";

/* eslint-disable jsx-a11y/anchor-is-valid */
import { hideLoader } from "action/loader/hideLoader";
import { IUrlInformation } from "api/interfaces/get-page-details/common/nested-pagecontent.type";
import { IManufacturerActualResponse, IManufacturerPageContent } from "api/interfaces/get-page-details/manufacturer-page.type";
import NewPageHeader from "component/banner/NewPageHeader";
import PagePlaceholder, { PLACEHOLDER_PAGE_TYPE } from "component/category/PagePlaceholder";
import TabSelector from "component/design-system/tab-selector/TabSelector";
import { getTotalFilters } from "component/product/product-container-util";
import SearchBar from "component/searchBar/SearchBar";
import { LAYOUT } from "constant/layout-constant";
import useScreenSize from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import { VALID_QUERY_FILTERS } from "modules/category/constants/filter-constants";
import PageContainer from "modules/components/PageContainer";
import useNonInitialEffect from "modules/lib/hooks/useNonInitialEffect";
import Breadcrumbs from "modules/product/header/Breadcrumbs";
import MetaHead from "modules/site-structure/common/MetaHead";
import { DEFAULT_MAKE_MODEL_BANNER } from "modules/site-structure/common/make-model-year-utils";
import RattingScript from "modules/third-party/sitejabber/RattingScript";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import qs from "query-string";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { deepFind } from "util/helper/deepFind";
import { capitalize } from "util/lodash/capitalize";
import { refreshData } from "util/refreshData";
import { scrollTo } from "util/scroll-helper";
import autocomplete from "util/search/autoComplete";
import { getDynamicUrlPrefixSearch } from "util/search/getDynamicUrlPrefixSearch";
import { getFitmentOptionQS } from "util/vehicle/get-fitment-option-q-s";
import FaqAccordion from "../FaqAccordion";
import Categories from "./Categories";
import Manufacturer from "./Manufacturer";
import OverViewTab from "./OverViewTab";
import Products from "./Products";
import SliderCard from "./SliderCard";
import { MANUFACTURER_GROUPS } from "./manufacturer-constants";
const SsrProductContainerCompact = dynamic(
  () => import(/* webpackChunkName: "ssr-product-container-compact" */"component/category/SsrProductContainerCompact"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CATEGORY_PAGE} /> },
);
const SsrProductContainerRegular = dynamic(
  () => import(/* webpackChunkName: "ssr-product-container-regular" */"component/category/SsrProductContainerRegular"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CATEGORY_PAGE} /> },
);
const VideoSection = dynamic(() => import(/* webpackChunkName: "video-section" */ "component/landing-page/VideoSection"));

interface IProps {
  urlInformation: IUrlInformation;
  staticContent: IManufacturerPageContent;
  actualResponse: IManufacturerActualResponse;
  pageType: string;
  isSSR: boolean;
}

let CSR_DONE = false;

function ManufacturerContainer(props: IProps) {
  const {
    actualResponse, urlInformation, staticContent, isSSR, pageType,
  } = props;
  const {
    productsAggregation, fitmentOptionsDetails, fitmentOptionsPrompt, breadCrumbsV2,
  } = actualResponse;
  const { links = {}, pageDetails = {}, videos } = staticContent;
  const { manufacturer } = urlInformation;
  const isManufacturerGroup = Object.keys(MANUFACTURER_GROUPS).includes(manufacturer);

  const { t } = useTranslation("category");
  const commonT = (_: string) => t(_, { ns: "common" });
  const browser = useSelector((store: RootState) => store.browser);
  const router = useRouter();
  const dispatch = useDispatch();
  const [isMobile] = useScreenSize();
  const [tabs, setTabs] = useState(staticContent?.pageStructure && staticContent.pageStructure[0]?.article?.content ? [{
    title: t("Overview"),
    content: <OverViewTab items={staticContent.pageStructure} />,
  }] : []);

  const { pathname } = router.query;
  const isSimilarManufacturer = Object.keys(MANUFACTURER_GROUPS).some((key) => MANUFACTURER_GROUPS[key].some((el) => el.url.substring(1) === pathname));
  const [clientSidelatestData, setLatestData] = useState({});

  const { metaData } = staticContent;
  const { manufacturerTier1 = [], manufacturerTier2 = [], contentIds = [] } = links;
  const faqs = contentIds && contentIds
    .find((eachContent: any) => eachContent?.uiDetails?.uiType === "FAQ")?.uiDetails.content.faqs;
  const capitalManufacturer = capitalize(manufacturer);

  const categories = manufacturerTier1.concat(manufacturerTier2).map((category: any) => ({
    url: category.url,
    title: category.pageDetails.tier1,
    imgSrc: category.linkDetails?.image,
    tier0: category.pageDetails.tier0,
    tier1: category.pageDetails.tier1,
    tier2: category.pageDetails.tier2,
  }));
  const url = "/[pathname]";
  const { products: productList, ...filterOptions } = clientSidelatestData.productsAggregation || productsAggregation || {};
  const query = process.browser ? qs.parse(location.search) : router.query;
  const { selectedVehicle, isLoading: loadingGarage } = useSelector((state: RootState) => state.garage);
  const appliedFilters = getTotalFilters(
    commonT,
    query,
    {},
    selectedVehicle,
    {},
  );

  const { pageDisplayableTitle = "" } = staticContent.pageDetails || {};

  const propsToPass = {
    fitmentOptionsDetails: clientSidelatestData?.fitmentOptionsDetails || fitmentOptionsDetails,
    defaultLayout: LAYOUT.COMPACT,
    productList,
    filterOptions,
    url,
    pageType,
    appliedFilters,
    staticContent,
    pageDisplayableTitle,
    filterParams: {
      manufacturers: {
        enabled: false,
      },
    },
  };

  const getTabs = () => {
    const manufacturerTabs = [...tabs];
    if (videos?.length) {
      manufacturerTabs.push({ title: t("Videos"), content: <VideoSection videos={videos} browser={browser} /> });
    }
    if (faqs?.length && pathname !== "liqui-moly") { // Hide FAQ for liqui-moly
      manufacturerTabs.push({ title: t("FAQ"), content: <FaqAccordion faqs={faqs} /> });
    }
    setTabs(manufacturerTabs);
  };
  const scroll = () => {
    const includesValidFilter = Object.keys(query).filter((param: string) => VALID_QUERY_FILTERS.includes(param));
    if (includesValidFilter.length > 0) {
      scrollTo("PRODUCTS", {
        duration: 10, delay: 10, smooth: true, offset: -130,
      });
    }
  };
  useEffect(() => {
    scroll();
  });

  const { fitmentOptions } = selectedVehicle || {};
  const fitmentOptionQs = getFitmentOptionQS(fitmentOptions);
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    if (loadingGarage) {
      return;
    }
    if (!CSR_DONE && isSSR) {
      CSR_DONE = true;

      refreshData({
        pathname,
        isMobile,
        selectedVehicle,
      }).then(({ keysToUpdate }) => {
        setData(keysToUpdate);
      });
    }
  }, [loadingGarage]);

  useNonInitialEffect(() => {
    refreshData({
      pathname,
      isMobile,
      selectedVehicle,
    }).then(({ keysToUpdate }) => {
      setData(keysToUpdate);
    });
  }, [selectedVehicle, fitmentOptionQs]);

  useEffect(() => {
    setLatestData({});
  }, [router.query]);

  useEffect(() => {
    getTabs();
  }, []);

  function setData(keysToUpdate: any) {
    setLatestData(keysToUpdate);
    dispatch(hideLoader());
  }

  function searchApiHandler(searchPhrase: string) {
    autocomplete(searchPhrase, null, capitalManufacturer, { "record-type": "part_number" }).then(({ data }) => {
      setSearchResults(Array.isArray(data) ? data : []);
    });
  }

  function onClickSearchResult(eachSuggestionInner) {
    getDynamicUrlPrefixSearch(eachSuggestionInner, selectedVehicle, router);
  }

  return (
    <>

      <MetaHead
        title={`${metaData?.title || capitalManufacturer || ""}`}
        description={metaData?.description}
        keywords={metaData?.keywordsV2}
        contentIds={contentIds}
        robots={isSimilarManufacturer ? "noindex" : "all"}
        videos={videos}
      />

      <RattingScript />

      <PageContainer ShowGreyOuterContainer style={{ overflow: "hidden" }} fullWidth>

        <Breadcrumbs
          {...{
            breadCrumbsV2, staticContent,
          }}
        />

        <div>
          <NewPageHeader
            fitmentOptionsDetails={fitmentOptionsDetails}
            fitmentOptionsPrompt={fitmentOptionsPrompt}
            staticContent={staticContent}
            bannerData={{
              bannerImageUrl: deepFind(pageDetails, "bannerImageUrl", DEFAULT_MAKE_MODEL_BANNER),
              fallbackImage: DEFAULT_MAKE_MODEL_BANNER,
            }}
            pageDisplayableTitle={pageDisplayableTitle}
          />
        </div>

        <SearchBar
          searchValueHandler={searchApiHandler}
          results={searchResults}
          onClick={onClickSearchResult}
          heading={`Quick ${capitalManufacturer} Search`}
          placeholder={`Search ${capitalManufacturer} catalog by part number`}
          className="m-t-40"
        />
        <HideIf show={isManufacturerGroup}>
          <SliderCard {...{
            categoryUrls: MANUFACTURER_GROUPS[manufacturer],
            title: t("Browse Similar Manufacturers"),
            titleWidth: 350,
          }}
          >
            <Manufacturer />
          </SliderCard>
        </HideIf>

        <HideIf show={categories.length}>

          <Categories categories={categories} manufacturer={capitalManufacturer} />
        </HideIf>

        <HideIf show={links.productLines}>

          <Products productLines={links.productLines} />
        </HideIf>
        <TabSelector tabs={tabs} />
        <div id="PRODUCTS" className="row">
          {(isMobile) ? (<SsrProductContainerCompact {...propsToPass} />)
            : (
              <div className="col-xs-12">
                <SsrProductContainerRegular {...propsToPass} />
              </div>
            )}
        </div>
      </PageContainer>
    </>
  );
}

export default ManufacturerContainer;
